import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getAuthToken } from '../utils/auth';
import { ArrowLeft, Mail, Send, Loader2, Building, Plus, Users, BookOpen } from 'lucide-react';
import { ADVISOR_EMAIL_SERIES } from '../data/advisorEmailSeries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDealerEmailPage — the super admin composes emails to any dealer.
 * Left column: existing threads (one per dealer) + a "New Email" button.
 * Right column: selected thread's outbound + inbound messages + a compose
 * area at the bottom. New Email opens a dealer picker and asks for
 * subject + body_html.
 */
export default function AdminDealerEmailPage() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [pickerDealer, setPickerDealer] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/dealer-emails/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(res.data?.threads || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadDealers = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // /admin/dealers returns a bare array of dealer docs
      const list = Array.isArray(res.data) ? res.data : (res.data?.dealers || []);
      setDealers(list);
    } catch (e) { /* silent */ }
  };

  const loadThread = async (threadId) => {
    if (!threadId) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/dealer-emails/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(res.data?.emails || []);
      setThreads((prev) => prev.map(t => t.id === threadId ? { ...t, unread_for_admin: 0 } : t));
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadThreads();
    loadDealers();
    const iv = setInterval(loadThreads, 8000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadThread(selectedId);
    const iv = setInterval(() => loadThread(selectedId), 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [emails]);

  const sendEmail = async (dealerId) => {
    if (!dealerId || !subject.trim() || !body.trim()) {
      toast.error('Pick a dealer, subject, and body first');
      return;
    }
    setSending(true);
    try {
      const token = getAuthToken();
      const bodyHtml = body.replace(/\n/g, '<br/>');
      const res = await axios.post(
        `${API}/admin/dealers/${dealerId}/send-email`,
        { subject: subject.trim(), body_html: bodyHtml },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email sent');
      setSubject('');
      setBody('');
      setComposing(false);
      setPickerDealer('');
      // Reload threads then open the one we just wrote to (matched by dealer)
      await loadThreads();
      const dealerThread = (await axios.get(`${API}/admin/dealer-emails/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      })).data?.threads?.find(t => t.dealer_id === dealerId);
      if (dealerThread?.id) setSelectedId(dealerThread.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const selected = threads.find(t => t.id === selectedId);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16" data-testid="admin-email-page">
      <div className="border-b bg-white dark:bg-gray-900 px-6 py-4 flex items-center gap-3 sticky top-16 z-30">
        <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-gray-900 dark:hover:text-white">
          <ArrowLeft size={20} />
        </button>
        <Mail size={22} className="text-blue-600" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Email Advisors</h1>
        <button
          onClick={() => setComposing(true)}
          className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5"
          data-testid="admin-new-email-btn"
        >
          <Plus size={14} />
          New Email
        </button>
      </div>

      {composing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl p-6 space-y-3" data-testid="admin-new-email-modal">
            <div className="flex items-center gap-2 border-b pb-3">
              <Users size={18} className="text-blue-600" />
              <h2 className="font-bold text-gray-900 dark:text-white">Compose Advisor Email</h2>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">To Advisor</label>
              <select
                value={pickerDealer}
                onChange={(e) => setPickerDealer(e.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                data-testid="admin-email-dealer-picker"
              >
                <option value="">— Select an advisor —</option>
                {dealers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.business_name || d.email} ({d.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
                <BookOpen size={12} /> Advisor Series Template (optional)
              </label>
              <select
                value=""
                onChange={(e) => {
                  const t = ADVISOR_EMAIL_SERIES.find((s) => s.id === e.target.value);
                  if (!t) return;
                  const d = dealers.find((x) => x.id === pickerDealer);
                  const biz = d?.business_name || d?.email || '{business}';
                  setSubject(t.subject);
                  setBody(t.body.replaceAll('{business}', biz));
                }}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                data-testid="admin-email-series-picker"
              >
                <option value="">— Load from the 5-part advisor series —</option>
                {ADVISOR_EMAIL_SERIES.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                data-testid="admin-email-subject"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Line breaks preserved. HTML is escaped so you can just type."
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white resize-none"
                data-testid="admin-email-body"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setComposing(false); setSubject(''); setBody(''); setPickerDealer(''); }}
                className="px-3 py-1.5 rounded-lg border text-sm"
                data-testid="admin-email-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => sendEmail(pickerDealer)}
                disabled={sending || !pickerDealer || !subject.trim() || !body.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
                data-testid="admin-email-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[calc(100vh-73px-64px)]">
        <aside className="border-r bg-white dark:bg-gray-900 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-gray-400"><Loader2 className="animate-spin mx-auto" /></div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              No dealer threads yet. Click <strong>New Email</strong> above to send your first one.
            </div>
          ) : (
            threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-4 py-3 border-b transition-colors ${
                  selectedId === t.id
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
                data-testid={`admin-email-thread-${t.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Building size={14} className="text-gray-400" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {t.business_name || t.dealer_email}
                    </span>
                  </div>
                  {t.unread_for_admin > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                      {t.unread_for_admin}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {t.last_email?.subject ? `${t.last_email.direction === 'outbound' ? '→' : '←'} ${t.last_email.subject}` : '—'}
                </p>
              </button>
            ))
          )}
        </aside>

        <section className="flex flex-col bg-white dark:bg-gray-900">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm text-center px-8">
              {threads.length === 0
                ? 'Click "New Email" above to start a conversation with a dealer.'
                : 'Pick a thread on the left to view its emails.'}
            </div>
          ) : (
            <>
              <div className="border-b px-6 py-3">
                <span className="font-semibold text-gray-900 dark:text-white">
                  {selected?.business_name || selected?.dealer_email}
                </span>
                <span className="text-xs text-gray-400 ml-2">{selected?.dealer_email}</span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {emails.map((m) => {
                  const mine = m.direction === 'outbound';
                  return (
                    <div key={m.id} className={`rounded-lg border ${mine
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'} p-4`}
                      data-testid={`admin-email-${m.id}`}>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className="font-semibold">
                          {mine ? `You → ${m.to_email}` : `${m.from_name || m.from_email} → You`}
                        </span>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        {m.subject}
                      </div>
                      <div
                        className="text-sm text-gray-700 dark:text-gray-200 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: m.html_body || m.body_html || `<pre>${m.text_body || ''}</pre>` }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
